import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import {
  BrowserRouter,
  Navigate,
  NavLink,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';
import {
  AlertTriangle,
  ArrowDownToLine,
  ArrowRight,
  ArrowUpFromLine,
  Bell,
  CalendarDays,
  Check,
  ChevronLeft,
  ChevronRight,
  CircleUserRound,
  Clock3,
  CloudCog,
  FileClock,
  Gauge,
  LayoutDashboard,
  LogOut,
  Mail,
  Menu,
  Plane,
  Plus,
  RefreshCw,
  Search,
  Settings,
  ShieldCheck,
  SlidersHorizontal,
  Trash2,
  Users,
  X,
} from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import './styles.css';
import {
  appCheckConfigured,
  firebaseConfigured,
  googleLogin,
  logout,
  observeAuth,
  removeTransfer,
  refreshProfile,
  refreshTransferStatus,
  lookupFlight,
  changeUserAccess,
  diagnoseEmailConfig,
  loadSettings,
  saveSettings,
  sendTestFlightEmail,
  subscribeAeroStatus,
  subscribeAuditLogs,
  saveTransfer,
  subscribeNotifications,
  subscribeStatusLog,
  subscribeTransfers,
  subscribeUsers,
  type AeroDataBoxCredits,
  type AuditLogDocument,
  type NotificationDocument,
  type StatusLogEntry,
  type TransferDocument,
  type UserProfile,
  type User,
} from './firebase';

export type TransferDirection = 'AIRPORT_PICKUP' | 'AIRPORT_DROPOFF';
export type TransferFlightStatus =
  | 'DRAFT'
  | 'SCHEDULED'
  | 'ON_TIME'
  | 'DELAYED'
  | 'GATE_CLOSED'
  | 'DEPARTED'
  | 'LANDED'
  | 'CANCELLED'
  | 'DIVERTED'
  | 'DATA_STALE'
  | 'COMPLETED';
// The flight's lifecycle phase, independent of whether it's running on time - shown as a separate
// badge next to the always-visible on-time/delayed indicator instead of one hiding the other.
export type TransferPhase =
  'SCHEDULED' | 'GATE_CLOSED' | 'DEPARTED' | 'LANDED' | 'CANCELLED' | 'DIVERTED' | 'DATA_STALE';
type Transfer = {
  id: string;
  flightNumber: string;
  route: string;
  direction: TransferDirection;
  scheduled: string;
  estimated: string;
  // The provider's scheduled time differs from the entered one (customer gave a wrong/inexact
  // time). Not a delay - that comes only from computed.delayMinutes.
  estimatedDiffers: boolean;
  providerScheduled: string;
  delay: number;
  status: TransferFlightStatus;
  phase: TransferPhase;
  stale: boolean;
  delayUnknown: boolean;
  delayConflict: boolean;
  probable?: ProbableDelay;
  driver: string;
  reference: string;
  passenger: string;
  serviceDate: string;
  flightTime: string;
  driverEmail: string;
  primaryEmail: string;
  pickupBuffer: number;
  notes: string;
  providerLastError?: string;
  providerMatchCount?: number;
  providerLastCheckedAt?: Date;
};
// Secondary, unconfirmed delay derived from the aircraft's inbound leg (computed.inbound).
type ProbableDelay = {
  minutes: number;
  confidence: string;
  inboundFlightNumber?: string;
  inboundFrom?: string;
  inboundEta?: string;
  // Earlier leg of the aircraft's day the delay is carried over from.
  upstreamFlightNumber?: string;
  upstreamFrom?: string;
  reg?: string;
  aircraftChanged: boolean;
};
const confidenceLabel: Record<string, string> = {
  HIGH: 'magas',
  MEDIUM: 'közepes',
  LOW: 'alacsony',
};
function probableDelayDescription(probable: ProbableDelay) {
  const inbound = probable.inboundFlightNumber
    ? `A beérkező gép (${probable.inboundFlightNumber}${probable.inboundFrom ? `, ${probable.inboundFrom} felől` : ''}) várható érkezése ${timePart(probable.inboundEta) || '—'}.`
    : 'A gép a nap első járata.';
  const upstream = probable.upstreamFlightNumber
    ? ` A késés a gép korábbi útjából ered (${probable.upstreamFlightNumber}${probable.upstreamFrom ? `, ${probable.upstreamFrom} felől` : ''}).`
    : '';
  return `Nem megerősített becslés. ${inbound}${upstream} Megbízhatóság: ${confidenceLabel[probable.confidence] ?? probable.confidence}.`;
}
const statusMap: Record<TransferFlightStatus, { label: string; icon: string }> = {
  DRAFT: { label: 'Piszkozat', icon: '○' },
  SCHEDULED: { label: 'Ütemezve', icon: '◷' },
  ON_TIME: { label: 'Időben', icon: '✓' },
  DELAYED: { label: 'Késik', icon: '!' },
  GATE_CLOSED: { label: 'Beszállás lezárva', icon: '↑' },
  DEPARTED: { label: 'Elindult / úton', icon: '↑' },
  LANDED: { label: 'Leszállt', icon: '↓' },
  CANCELLED: { label: 'Törölve', icon: '×' },
  DIVERTED: { label: 'Eltérítve', icon: '↗' },
  DATA_STALE: { label: 'Nincs friss adat', icon: '?' },
  COMPLETED: { label: 'Lezárva', icon: '✓' },
};
// Phase badge: only "where the flight is" (scheduled/gate closed/departed/landed, or a terminal
// cancelled/diverted) - reuses the same labels/icons as statusMap, just without the timing values.
const phaseMap: Record<TransferPhase, { label: string; icon: string }> = {
  SCHEDULED: statusMap.SCHEDULED,
  GATE_CLOSED: statusMap.GATE_CLOSED,
  DEPARTED: statusMap.DEPARTED,
  LANDED: statusMap.LANDED,
  CANCELLED: statusMap.CANCELLED,
  DIVERTED: statusMap.DIVERTED,
  DATA_STALE: statusMap.DATA_STALE,
};
const schema = z.object({
  date: z.string().min(1, 'A dátum kötelező'),
  flightNumber: z.string().min(3, 'Adj meg érvényes járatszámot'),
  origin: z.string().optional(),
  destination: z.string().optional(),
  flightTime: z.string().min(1, 'Kötelező'),
  direction: z.enum(['AIRPORT_PICKUP', 'AIRPORT_DROPOFF']),
  email: z.email('Érvénytelen e-mail-cím'),
  driver: z.string().optional(),
  driverEmail: z.union([z.literal(''), z.email('Érvénytelen e-mail-cím')]),
  reference: z.string().optional(),
  passenger: z.string().optional(),
  pickupBuffer: z.number().min(0).max(240),
  notes: z.string().optional(),
});
type FormData = z.infer<typeof schema>;

function getLoginErrorMessage(error: unknown) {
  if (!appCheckConfigured)
    return 'A Google-belépéshez előbb be kell állítani a reCAPTCHA Enterprise site key-t.';
  const message = error instanceof Error ? error.message : String(error);
  if (message.includes('popup-closed-by-user'))
    return 'A belépési ablak bezárult a bejelentkezés befejezése előtt.';
  if (message.includes('auth/unauthorized-domain'))
    return 'Ez a domain nincs engedélyezve a Firebase Authentication beállításaiban.';
  if (message.includes('internal') || message.includes('functions/'))
    return 'A Google-belépés sikerült, de a hozzáférési profil ellenőrzése nem érhető el. Próbáld újra pár perc múlva.';
  return 'A belépés nem sikerült. Ellenőrizze, hogy ez a Google-fiók engedélyezve van-e.';
}

function normalizeErrorText(message: string) {
  return message
    .replaceAll('Ã¡', 'á')
    .replaceAll('Ã©', 'é')
    .replaceAll('Ã­', 'í')
    .replaceAll('Ã³', 'ó')
    .replaceAll('Ã¶', 'ö')
    .replaceAll('Ãº', 'ú')
    .replaceAll('Ã¼', 'ü')
    .replaceAll('Å‘', 'ő')
    .replaceAll('Å±', 'ű')
    .replaceAll('Ã', 'Á')
    .replaceAll('Ã‰', 'É')
    .replaceAll('Ã–', 'Ö')
    .replaceAll('Ãœ', 'Ü');
}

function Brand({ compact = false }: { compact?: boolean }) {
  return (
    <div className="brand">
      <span className="brand-mark">
        <Plane size={20} />
      </span>
      {!compact && (
        <div>
          <strong>SKYFLY</strong>
          <small>OPS / CONTROL</small>
        </div>
      )}
    </div>
  );
}
function Login() {
  const nav = useNavigate();
  const [loginError, setLoginError] = useState('');
  const [loggingIn, setLoggingIn] = useState(false);

  const handleLogin = async () => {
    try {
      setLoggingIn(true);
      setLoginError('');
      const result = await googleLogin();
      nav(result.profile?.status === 'PENDING' ? '/pending' : '/daily');
    } catch (error) {
      setLoginError(getLoginErrorMessage(error));
    } finally {
      setLoggingIn(false);
    }
  };
  return (
    <main className="login">
      <section className="login-art">
        <Brand />
        <div className="radar">
          <span />
          <span />
          <span />
          <Plane />
        </div>
        <div className="login-copy">
          <span className="eyebrow">MŰVELETI RENDSZER</span>
          <h1>
            Minden járat.
            <br />
            <em>Minden pillanat.</em>
          </h1>
          <p>Transzferirányítás és valós idejű járatfigyelés egyetlen, nyugodt irányítópulton.</p>
          <div className="trust">
            <span>
              <ShieldCheck />
              Biztonságos belépés
            </span>
            <span>
              <Clock3 />
              24/7 üzem
            </span>
          </div>
        </div>
      </section>
      <section className="login-panel">
        <div className="login-box">
          <div className="mobile-brand">
            <Brand />
          </div>
          <span className="eyebrow dark">ÜDVÖZÖLJÜK ÚJRA</span>
          <h2>Belépés a vezérlőbe</h2>
          <p>A folytatáshoz használja a SkyFly Travel Google-fiókját.</p>
          <button className="google" onClick={handleLogin} disabled={loggingIn}>
            <span>G</span>Belépés Google-fiókkal
            <ArrowRight />
          </button>
          {loginError && (
            <div className="login-error" role="alert">
              <AlertTriangle />
              {loginError}
            </div>
          )}
          <div className="secure-note">
            <ShieldCheck />
            <span>A hozzáférés engedélyhez kötött és naplózott.</span>
          </div>
          <a href="https://www.skyflytravel.hu/adatvedelmi-szabalyzat/">Adatvédelmi tájékoztató</a>
        </div>
        <small className="copyright">
          © 2026 SkyFly Travel · Belső használatú rendszer · v{__APP_VERSION__}
        </small>
      </section>
    </main>
  );
}
function Pending({ user }: { user: User }) {
  const nav = useNavigate();
  const [error, setError] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      setError('');
      const result = await refreshProfile();
      if (result.profile.status === 'ACTIVE') nav('/daily', { replace: true });
    } catch {
      setError(
        !appCheckConfigured
          ? 'A folytatáshoz előbb állítsd be a reCAPTCHA Enterprise site key-t.'
          : 'A profil frissítése nem sikerült. Próbáld újra később.',
      );
    } finally {
      setRefreshing(false);
    }
  };
  const handleLogout = async () => {
    await logout();
    nav('/login', { replace: true });
  };
  return (
    <main className="pending">
      <Brand />
      <div className="pending-card">
        <div className="orbit">
          <Clock3 />
        </div>
        <span className="eyebrow dark">HOZZÁFÉRÉS FOLYAMATBAN</span>
        <h1>Jóváhagyásra vár</h1>
        <p>
          A hozzáférés adminisztrátori jóváhagyásra vár. Amint megtörtént, e-mailben értesítjük.
        </p>
        <div className="identity">
          <CircleUserRound />
          <div>
            <strong>{user.displayName || user.email || 'SkyFly felhasználó'}</strong>
            <small>{user.email}</small>
          </div>
        </div>
        <button className="primary" onClick={handleRefresh} disabled={refreshing}>
          <RefreshCw />
          {refreshing ? 'Frissítés…' : 'Státusz frissítése'}
        </button>
        {error && (
          <div className="login-error" role="alert">
            {error}
          </div>
        )}
        <button className="text-button" onClick={handleLogout}>
          <LogOut />
          Kijelentkezés
        </button>
      </div>
    </main>
  );
}
const navItems = [
  ['/daily', 'Napi irányítás', LayoutDashboard, false],
  ['/stats', 'Statisztikák', Gauge, false],
  ['/settings', 'Beállításaim', Settings, false],
  ['/admin/users', 'Felhasználók', Users, true],
  ['/admin/settings', 'Rendszerbeállítások', SlidersHorizontal, true],
  ['/admin/aerodatabox', 'AeroDataBox', CloudCog, true],
  ['/admin/audit', 'Auditnapló', FileClock, true],
] as const;
function initials(user: User) {
  const source = user.displayName?.trim() || user.email?.split('@')[0] || 'SF';
  return source
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('');
}

function dateFromFirestore(value: any) {
  return value?.toDate?.() as Date | undefined;
}

function notificationTitle(item: NotificationDocument) {
  if (typeof item.title === 'string' && item.title.trim()) return item.title;
  if (item.eventType === 'DELAY') {
    const level = typeof item.level === 'string' ? item.level : 'OPERATIONAL';
    return level === 'SEVERE'
      ? 'Súlyos járatkésés'
      : level === 'CRITICAL'
        ? 'Kritikus járatkésés'
        : 'Járatkésés';
  }
  if (item.status === 'FAILED') return 'Értesítés küldése sikertelen';
  if (item.status === 'SENT') return 'Értesítés elküldve';
  return 'Rendszerértesítés';
}

function notificationDetail(item: NotificationDocument) {
  if (typeof item.message === 'string' && item.message.trim()) return item.message;
  const flight = item.flightNumber ?? item.transfer?.flightNumber ?? item.transferId;
  const estimated = item.estimatedTime ?? item.estimatedEventUtc;
  const parts = [
    flight ? `Járat: ${flight}` : '',
    typeof item.level === 'string' ? `Szint: ${item.level}` : '',
    typeof estimated === 'string' ? `Idő: ${timePart(estimated)}` : '',
  ].filter(Boolean);
  return parts.join(' · ') || 'Új operációs esemény érkezett.';
}

function auditActor(item: AuditLogDocument) {
  return item.actorEmail ?? item.actorUid ?? 'Rendszer';
}

function auditTarget(item: AuditLogDocument) {
  const target = [item.targetType, item.targetId].filter(Boolean).join(': ');
  if (target) return target;
  if (typeof item.newValue?.reference === 'string') return item.newValue.reference;
  if (typeof item.previousValue?.reference === 'string') return item.previousValue.reference;
  return 'Nincs célobjektum';
}

function auditTime(item: AuditLogDocument) {
  const date = dateFromFirestore(item.timestamp);
  return date
    ? date.toLocaleString('hu-HU', {
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      })
    : 'Időpont nélkül';
}

// Mirrors functions/src/index.ts isFinalTransferStatus: AIRPORT_DROPOFF (address -> airport)
// cares about departure, AIRPORT_PICKUP (airport -> address) cares about arrival.
function isFinalTransferStatus(status: string, direction: TransferDirection) {
  if (status === 'CANCELLED' || status === 'DIVERTED') return true;
  if (direction === 'AIRPORT_DROPOFF')
    return status === 'GATE_CLOSED' || status === 'DEPARTED' || status === 'LANDED';
  return status === 'LANDED';
}
const DISMISSED_NOTIFICATIONS_KEY = 'skyfly.dismissedNotifications';

function loadDismissedNotifications(): Set<string> {
  try {
    const raw = localStorage.getItem(DISMISSED_NOTIFICATIONS_KEY);
    return new Set(raw ? (JSON.parse(raw) as string[]) : []);
  } catch {
    return new Set();
  }
}

function saveDismissedNotifications(ids: Set<string>) {
  try {
    localStorage.setItem(DISMISSED_NOTIFICATIONS_KEY, JSON.stringify([...ids]));
  } catch {
    // ignore storage errors (private browsing, quota, ...)
  }
}

function NotificationsMenu({ onStatus }: { onStatus?: (ok: boolean) => void }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [rawItems, setRawItems] = useState<NotificationDocument[]>([]);
  const [transfers, setTransfers] = useState<TransferDocument[]>([]);
  const [error, setError] = useState('');
  const [now, setNow] = useState(() => Date.now());
  const [dismissed, setDismissed] = useState<Set<string>>(() => loadDismissedNotifications());
  useEffect(
    () =>
      subscribeNotifications(
        (rows) => {
          setRawItems(rows);
          setError('');
          onStatus?.(true);
        },
        () => {
          setError('Az értesítések betöltése nem sikerült.');
          onStatus?.(false);
        },
      ),
    [onStatus],
  );
  useEffect(() => subscribeTransfers(setTransfers, () => undefined), []);
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(timer);
  }, []);
  useEffect(() => {
    setDismissed((prev) => {
      const ids = new Set(rawItems.map((item) => item.id));
      const next = new Set([...prev].filter((id) => ids.has(id)));
      if (next.size === prev.size) return prev;
      saveDismissedNotifications(next);
      return next;
    });
  }, [rawItems]);
  const transfersById = useMemo(() => new Map(transfers.map((t) => [t.id, t])), [transfers]);
  const items = useMemo(
    () =>
      rawItems.filter((item) => {
        if (dismissed.has(item.id)) return false;
        const transferId = item.transferId ?? item.transfer?.id;
        const transfer = transferId ? transfersById.get(transferId) : undefined;
        if (transfer?.active === false) return false;
        if (
          transfer &&
          isFinalTransferStatus(transfer.computed?.status, transfer.direction ?? 'AIRPORT_PICKUP')
        )
          return false;
        const eventTime = item.estimatedTime ?? item.scheduledTime ?? item.estimatedEventUtc;
        if (typeof eventTime === 'string') {
          const eventMs = new Date(eventTime).getTime();
          if (Number.isFinite(eventMs) && eventMs < now) return false;
        }
        return true;
      }),
    [rawItems, transfersById, now, dismissed],
  );
  const visibleCount = items.length;
  const goToTransfer = (item: NotificationDocument) => {
    const transferId = item.transferId ?? item.transfer?.id;
    if (!transferId) return;
    setOpen(false);
    navigate(`/transfer/${transferId}`);
  };
  const dismissNotification = (id: string) => {
    setDismissed((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      saveDismissedNotifications(next);
      return next;
    });
  };
  const dismissAll = () => {
    if (!items.length) return;
    setDismissed((prev) => {
      const next = new Set(prev);
      items.forEach((item) => next.add(item.id));
      saveDismissedNotifications(next);
      return next;
    });
  };
  return (
    <div className="notifications">
      <button
        type="button"
        aria-label={`Értesítések${visibleCount ? `: ${visibleCount} új` : ''}`}
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        <Bell />
        {visibleCount > 0 && <b>{visibleCount > 9 ? '9+' : visibleCount}</b>}
      </button>
      {open && (
        <div className="notifications-panel" role="status">
          <div className="notifications-head">
            <strong>Értesítések</strong>
            <div className="notifications-head-actions">
              <small>{visibleCount ? `${visibleCount} legfrissebb` : 'Nincs új esemény'}</small>
              {visibleCount > 0 && (
                <button type="button" className="notifications-dismiss-all" onClick={dismissAll}>
                  Összes elfogadása
                </button>
              )}
            </div>
          </div>
          {error ? (
            <p className="notifications-empty">{error}</p>
          ) : items.length ? (
            <ul>
              {items.map((item) => {
                const createdAt = dateFromFirestore(item.createdAt);
                const transferId = item.transferId ?? item.transfer?.id;
                const content = (
                  <>
                    <strong>{notificationTitle(item)}</strong>
                    <p>{notificationDetail(item)}</p>
                    <small>
                      {createdAt
                        ? createdAt.toLocaleString('hu-HU', {
                            month: '2-digit',
                            day: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit',
                          })
                        : 'Időpont nélkül'}
                    </small>
                  </>
                );
                return (
                  <li key={item.id}>
                    <span
                      className={`notification-dot ${String(item.level ?? item.status ?? '').toLowerCase()}`}
                    />
                    {transferId ? (
                      <button
                        type="button"
                        className="notification-link"
                        onClick={() => goToTransfer(item)}
                      >
                        {content}
                      </button>
                    ) : (
                      <div>{content}</div>
                    )}
                    <button
                      type="button"
                      className="notification-dismiss"
                      aria-label="Értesítés elfogadása"
                      title="Elfogadás"
                      onClick={() => dismissNotification(item.id)}
                    >
                      <Check />
                    </button>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="notifications-empty">Nincs friss operációs értesítés.</p>
          )}
        </div>
      )}
    </div>
  );
}

function Shell({ user, isAdmin }: { user: User; isAdmin: boolean }) {
  const [open, setOpen] = useState(false);
  const [pendingUsers, setPendingUsers] = useState(0);
  const [networkOnline, setNetworkOnline] = useState(
    typeof navigator === 'undefined' ? true : navigator.onLine,
  );
  const [firestoreHealthy, setFirestoreHealthy] = useState(true);
  const loc = useLocation();
  const nav = useNavigate();
  const userInitials = initials(user);
  const visibleNavItems = navItems.filter(([, , , adminOnly]) => !adminOnly || isAdmin);
  useEffect(
    () =>
      isAdmin
        ? subscribeUsers(
            (rows) => setPendingUsers(rows.filter((row) => row.status === 'PENDING').length),
            () => setPendingUsers(0),
          )
        : undefined,
    [isAdmin],
  );
  useEffect(() => {
    const goOnline = () => setNetworkOnline(true);
    const goOffline = () => setNetworkOnline(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, []);
  const systemOnline = networkOnline && firestoreHealthy;
  const handleLogout = async () => {
    await logout();
    nav('/login', { replace: true });
  };
  return (
    <div className="app">
      <aside className={open ? 'sidebar open' : 'sidebar'}>
        <div className="side-head">
          <Brand />
          <button aria-label="Menü bezárása" onClick={() => setOpen(false)}>
            <X />
          </button>
        </div>
        <nav>
          {visibleNavItems.map(([to, label, Icon, adminOnly], i) => (
            <React.Fragment key={to}>
              {adminOnly && (i === 0 || !visibleNavItems[i - 1][3]) && (
                <span className="nav-label">ADMINISZTRÁCIÓ</span>
              )}
              <NavLink
                to={to}
                onClick={() => setOpen(false)}
                className={({ isActive }) => (isActive ? 'active' : '')}
              >
                <Icon />
                {label}
                {to === '/admin/users' && pendingUsers > 0 && <b>{pendingUsers}</b>}
              </NavLink>
            </React.Fragment>
          ))}
        </nav>
        <div className="side-foot">
          <div>
            <strong>{user.displayName || user.email || 'SkyFly felhasználó'}</strong>
            <small>{user.email}</small>
          </div>
          <button type="button" onClick={handleLogout} aria-label="Kijelentkezés">
            <LogOut />
          </button>
        </div>
      </aside>
      <div className="main">
        <header>
          <button className="menu" onClick={() => setOpen(true)} aria-label="Menü">
            <Menu />
          </button>
          <div>
            <NavLink to="/daily" className="crumb">
              SKYFLY OPS /
            </NavLink>{' '}
            <strong>{navItems.find((x) => x[0] === loc.pathname)?.[1] ?? 'TRANSZFER'}</strong>
          </div>
          <div className="header-right">
            <span className={systemOnline ? 'live' : 'live offline'} role="status">
              <i />
              {systemOnline ? 'RENDSZER ONLINE' : 'KAPCSOLAT MEGSZAKADT'}
            </span>
            <NotificationsMenu onStatus={setFirestoreHealthy} />
            <div className="avatar">{userInitials}</div>
          </div>
        </header>
        <Routes>
          <Route path="daily" element={<Daily user={user} />} />
          <Route path="stats" element={<StatsPage />} />
          <Route path="settings" element={<UserSettings user={user} />} />
          <Route
            path="admin/users"
            element={isAdmin ? <UsersPage /> : <Navigate to="/daily" replace />}
          />
          <Route
            path="admin/settings"
            element={isAdmin ? <AdminSettings /> : <Navigate to="/daily" replace />}
          />
          <Route
            path="admin/aerodatabox"
            element={isAdmin ? <AeroPage /> : <Navigate to="/daily" replace />}
          />
          <Route
            path="admin/audit"
            element={isAdmin ? <AuditPage /> : <Navigate to="/daily" replace />}
          />
          <Route path="transfer/:id" element={<Daily user={user} />} />
          <Route path="*" element={<Navigate to="daily" />} />
        </Routes>
      </div>
    </div>
  );
}
function Status({
  status,
  delay,
  criticalThreshold = 60,
}: {
  status: TransferFlightStatus;
  delay?: number;
  criticalThreshold?: number;
}) {
  return (
    <span
      className={`status s-${status.toLowerCase()} ${delay && delay >= criticalThreshold ? 'critical' : ''}`}
    >
      <i>{statusMap[status].icon}</i>
      {status === 'DELAYED' ? `${delay} perc késés` : statusMap[status].label}
    </span>
  );
}
// Renders flight state as two independent badges: on-time/delayed always shows, phase always
// shows next to it - so a delayed-but-departed flight reads as "Késik 25 perc" + "Elindult / úton"
// instead of one overwriting the other. CANCELLED/DIVERTED are terminal facts that make both the
// timing and the phase moot, so they collapse into a single badge. DATA_STALE never replaces the
// last known badges - it only fades them and adds a small note, since the values keep the last
// known reading rather than being reset when the provider has nothing fresh to report.
function FlightStatus({
  phase,
  delay,
  stale,
  delayUnknown,
  delayConflict,
  probable,
  criticalThreshold = 60,
}: {
  phase: TransferPhase;
  delay: number;
  stale?: boolean;
  delayUnknown?: boolean;
  delayConflict?: boolean;
  probable?: ProbableDelay;
  criticalThreshold?: number;
}) {
  if (phase === 'CANCELLED' || phase === 'DIVERTED') {
    return (
      <span className={`status s-${phase.toLowerCase()}`}>
        <i>{phaseMap[phase].icon}</i>
        {phaseMap[phase].label}
      </span>
    );
  }
  // The provider can report "Delayed" before it has a revised time - show that as a delay with an
  // unknown amount rather than letting the 0-minute value read as on time.
  const timing: 'ON_TIME' | 'DELAYED' = delay > 0 || delayUnknown ? 'DELAYED' : 'ON_TIME';
  const timingLabel =
    timing === 'ON_TIME'
      ? statusMap.ON_TIME.label
      : delay > 0
        ? `${delay} perc késés`
        : 'Késik (idő ismeretlen)';
  const shownPhase = phase === 'DATA_STALE' ? 'SCHEDULED' : phase;
  // Only while it says clearly more than the official delay (airlines often announce a delay in
  // small steps) and the aircraft hasn't left yet - after that the official figures are the
  // better source.
  const showProbable =
    probable &&
    probable.minutes >= 15 &&
    probable.minutes - Math.max(delay, 0) >= 15 &&
    phase !== 'DEPARTED' &&
    phase !== 'LANDED';
  return (
    <span className={`status-group ${stale ? 'stale' : ''}`}>
      <span
        className={`status s-${timing.toLowerCase()} ${delay >= criticalThreshold ? 'critical' : ''}`}
      >
        <i>{statusMap[timing].icon}</i>
        {timingLabel}
      </span>
      {showProbable && (
        <span
          className={`status s-probable ${probable.minutes >= criticalThreshold ? 'critical' : ''}`}
          title={probableDelayDescription(probable)}
        >
          <i>≈</i>
          {probable.minutes} perc valószínű késés
        </span>
      )}
      <span className={`status s-${shownPhase.toLowerCase()}`}>
        <i>{phaseMap[shownPhase].icon}</i>
        {phaseMap[shownPhase].label}
      </span>
      {stale && (
        <small className="stale-note" title="A szolgáltatótól nem érkezett friss adat">
          Nincs friss adat
        </small>
      )}
      {delayConflict && timing === 'DELAYED' && !stale && (
        <small
          className="stale-note"
          title="A szolgáltató legutóbb nem jelzett késést, de ezt még nem erősítette meg. A késést addig megtartjuk."
        >
          Ellentmondó adat, ellenőrzés alatt
        </small>
      )}
    </span>
  );
}
function timePart(value?: string) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value.slice(11, 16);
  return new Intl.DateTimeFormat('hu-HU', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: 'Europe/Budapest',
  }).format(date);
}
const opsDateKeyFormatter = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Europe/Budapest',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});
function localDateKey(date = new Date()) {
  return opsDateKeyFormatter.format(date);
}
const opsTimeKeyFormatter = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Europe/Budapest',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});
function localTimeKey(date = new Date()) {
  return opsTimeKeyFormatter.format(date);
}
function shiftDateKey(value: string, days: number) {
  const [year, month, day] = value.split('-').map(Number);
  return localDateKey(new Date(Date.UTC(year, month - 1, day + days, 12)));
}
function formatDateLabel(value: string) {
  const [year, month, day] = value.split('-').map(Number);
  return new Intl.DateTimeFormat('hu-HU', {
    dateStyle: 'full',
    timeZone: 'Europe/Budapest',
  }).format(new Date(Date.UTC(year, month - 1, day, 12)));
}
const shortDateFormatter = new Intl.DateTimeFormat('hu-HU', {
  month: 'short',
  day: 'numeric',
  timeZone: 'Europe/Budapest',
});
function shortDateLabel(value: string) {
  if (!value) return '—';
  const [year, month, day] = value.split('-').map(Number);
  return shortDateFormatter.format(new Date(Date.UTC(year, month - 1, day, 12)));
}
function zonedTimeToUtcIso(dateKey: string, time: string, timeZone: string) {
  const naiveUtc = new Date(`${dateKey}T${time}:00Z`);
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })
    .formatToParts(naiveUtc)
    .reduce<Record<string, string>>((acc, part) => {
      acc[part.type] = part.value;
      return acc;
    }, {});
  const asIfUtc = Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    Number(parts.hour === '24' ? '0' : parts.hour),
    Number(parts.minute),
    Number(parts.second),
  );
  const offset = asIfUtc - naiveUtc.getTime();
  return new Date(naiveUtc.getTime() - offset).toISOString();
}
function mapTransfer(item: TransferDocument): Transfer {
  const manual = item.manual ?? {};
  const computed = item.computed ?? {};
  const direction: TransferDirection = item.direction ?? 'AIRPORT_PICKUP';
  const manualEventUtc =
    direction === 'AIRPORT_DROPOFF' ? manual.scheduledDepartureUtc : manual.scheduledArrivalUtc;
  const scheduled = timePart(manualEventUtc);
  const liveEventUtc = computed.estimatedEventUtc ?? computed.scheduledEventUtc;
  // Compared with the provider's schedule, not its estimate, so a real delay doesn't read as a
  // wrongly entered time.
  const estimatedDiffers = Boolean(
    manualEventUtc &&
    computed.scheduledEventUtc &&
    new Date(manualEventUtc).getTime() !== new Date(computed.scheduledEventUtc).getTime(),
  );
  return {
    id: item.id,
    flightNumber: manual.flightNumber ?? '—',
    route: `${manual.originAirport ?? '—'} → ${manual.destinationAirport ?? '—'}`,
    direction,
    scheduled,
    estimated: timePart(liveEventUtc) || '—',
    estimatedDiffers,
    providerScheduled: timePart(computed.scheduledEventUtc ?? undefined),
    delay: computed.delayMinutes ?? 0,
    status: computed.status ?? 'DRAFT',
    phase: (computed.phase as TransferPhase) ?? 'SCHEDULED',
    stale: Boolean(computed.stale),
    delayUnknown: Boolean(computed.delayUnknown),
    delayConflict: Boolean(computed.delayConflict),
    probable:
      typeof computed.inbound?.probableDelayMinutes === 'number'
        ? {
            minutes: computed.inbound.probableDelayMinutes,
            confidence: String(computed.inbound.confidence ?? 'LOW'),
            inboundFlightNumber: computed.inbound.inboundFlightNumber,
            inboundFrom: computed.inbound.inboundFrom,
            inboundEta: computed.inbound.inboundEstimatedArrivalUtc,
            upstreamFlightNumber: computed.inbound.upstreamFlightNumber,
            upstreamFrom: computed.inbound.upstreamFrom,
            reg: computed.inbound.reg,
            aircraftChanged: Boolean(computed.inbound.aircraftChanged),
          }
        : undefined,
    driver: item.driver?.name || 'Nincs kiosztva',
    reference: item.reference || '—',
    passenger: item.passenger || '—',
    serviceDate: item.serviceDate ?? '',
    flightTime: scheduled,
    driverEmail: item.driver?.email ?? '',
    primaryEmail: item.primaryEmail ?? '',
    pickupBuffer: item.pickupBuffer ?? 35,
    notes: item.notes ?? '',
    providerLastError: item.provider?.lastError,
    providerMatchCount: Array.isArray(item.provider?.matches)
      ? item.provider.matches.length
      : undefined,
    providerLastCheckedAt: dateFromFirestore(item.provider?.lastCheckedAt),
  };
}
function Daily({ user }: { user: User }) {
  const params = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const [items, setItems] = useState<Transfer[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [direction, setDirection] = useState('ALL');
  const [statusFilter, setStatusFilter] = useState<TransferFlightStatus | 'ALL'>('ALL');
  const [selectedDate, setSelectedDate] = useState(() => localDateKey());
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState<Transfer | null>(null);
  const [dataError, setDataError] = useState('');
  const [deepLinkError, setDeepLinkError] = useState('');
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [defaultEmail, setDefaultEmail] = useState(user.email ?? '');
  const [criticalThreshold, setCriticalThreshold] = useState(60);
  const [defaultPickupBuffer, setDefaultPickupBuffer] = useState(35);
  const dateInputRef = useRef<HTMLInputElement>(null);
  useEffect(
    () =>
      subscribeTransfers(
        (rows) => {
          setItems(rows.map(mapTransfer));
          setDataError('');
          setLastUpdated(new Date());
          setLoading(false);
        },
        () => {
          setDataError('A transzferek betöltése nem sikerült.');
          setLoading(false);
        },
      ),
    [],
  );
  useEffect(
    () =>
      setEditing((current) => {
        if (!current) return current;
        const latest = items.find((item) => item.id === current.id);
        return latest ?? current;
      }),
    [items],
  );
  useEffect(() => {
    loadSettings('user', user.uid)
      .then((stored) => {
        const primaryEmail = stored?.primaryEmail;
        if (typeof primaryEmail === 'string' && primaryEmail) setDefaultEmail(primaryEmail);
      })
      .catch(() => {});
  }, [user.uid]);
  useEffect(() => {
    loadSettings('global')
      .then((stored) => {
        const value = Number(stored?.criticalDelay);
        if (Number.isFinite(value) && value >= 0) setCriticalThreshold(value);
        const buffer = Number(stored?.pickupBuffer);
        if (Number.isFinite(buffer) && buffer >= 0) setDefaultPickupBuffer(buffer);
      })
      .catch(() => {});
  }, []);
  useEffect(() => {
    if (!params.id || loading) return;
    const target = items.find((item) => item.id === params.id);
    if (target) {
      setEditing(target);
      setModal(true);
      setSelectedDate(target.serviceDate);
      setDeepLinkError('');
    } else {
      setDeepLinkError('A hivatkozott transzfer nem található, vagy törölve lett.');
    }
  }, [params.id, items, loading]);
  const searching = search.trim().length > 0;
  const filtered = useMemo(
    () =>
      items
        .filter(
          (t) =>
            (searching || t.serviceDate === selectedDate) &&
            (direction === 'ALL' || t.direction === direction) &&
            (statusFilter === 'ALL' || t.status === statusFilter) &&
            [t.flightNumber, t.driver, t.reference, t.passenger].some((v) =>
              v.toLowerCase().includes(search.toLowerCase()),
            ),
        )
        .sort((a, b) => {
          const aKey = a.estimated === '—' ? a.scheduled : a.estimated;
          const bKey = b.estimated === '—' ? b.scheduled : b.estimated;
          return `${a.serviceDate}T${aKey}`.localeCompare(`${b.serviceDate}T${bKey}`);
        }),
    [items, search, direction, statusFilter, selectedDate, searching],
  );
  const attention = filtered.filter((item) =>
    ['DELAYED', 'CANCELLED', 'DIVERTED', 'DATA_STALE'].includes(item.status),
  );
  const todayKey = localDateKey();
  const isTodaySelected = !searching && selectedDate === todayKey;
  const nextArrival = isTodaySelected
    ? [...filtered]
        .filter(
          (item) =>
            item.flightTime &&
            !['CANCELLED', 'COMPLETED'].includes(item.status) &&
            item.flightTime >= localTimeKey(),
        )
        .sort((a, b) => a.flightTime.localeCompare(b.flightTime))[0]
    : undefined;
  const lastApiCheck = filtered.reduce<Date | undefined>(
    (latest, item) =>
      item.providerLastCheckedAt && (!latest || item.providerLastCheckedAt > latest)
        ? item.providerLastCheckedAt
        : latest,
    undefined,
  );
  const selectedDateLabel = formatDateLabel(selectedDate);
  return (
    <main className="page">
      <div className="page-title">
        <div>
          <span className="eyebrow dark">OPERÁCIÓS ÁTTEKINTÉS</span>
          <h1>Napi irányítás</h1>
          <p>{searching ? 'Keresés az összes napra' : selectedDateLabel} · Europe/Budapest</p>
        </div>
        <button
          className="primary"
          onClick={() => {
            setEditing(null);
            setModal(true);
          }}
        >
          <Plus />
          Új transzfer
        </button>
      </div>
      <section className="metrics">
        <div>
          <span>AKTÍV TRANSZFEREK</span>
          <strong>{filtered.length}</strong>
          <small>
            <i className="green" />
            {
              filtered.filter((item) => !isFinalTransferStatus(item.status, item.direction)).length
            }{' '}
            figyelés alatt
          </small>
        </div>
        <div>
          <span>FIGYELMET IGÉNYEL</span>
          <strong className="orange">{attention.length}</strong>
          <small>
            {attention.filter((item) => item.delay >= criticalThreshold).length} kritikus késés
          </small>
        </div>
        <div>
          <span>KÖVETKEZŐ JÁRAT</span>
          <strong>{nextArrival?.flightTime || '—'}</strong>
          <small>
            {nextArrival
              ? `${nextArrival.flightNumber} · ${nextArrival.route}`
              : isTodaySelected
                ? 'Nincs ütemezett érkezés'
                : 'Csak a mai napra elérhető'}
          </small>
        </div>
        <div>
          <span>UTOLSÓ API LEKÉRDEZÉS</span>
          <strong>
            {lastApiCheck
              ? lastApiCheck.toLocaleTimeString('hu-HU', { hour: '2-digit', minute: '2-digit' })
              : '—'}
          </strong>
          <small>
            <i className={dataError ? 'red' : 'green'} />
            {dataError ? 'Kapcsolati hiba' : 'Firestore szinkron aktív'}
          </small>
        </div>
      </section>
      <section className="board">
        {dataError && (
          <div className="login-error" role="alert">
            {dataError}
          </div>
        )}
        {deepLinkError && (
          <div className="login-error" role="alert">
            {deepLinkError}
          </div>
        )}
        <div className="datebar">
          <button
            aria-label="Előző nap"
            onClick={() => setSelectedDate((date) => shiftDateKey(date, -1))}
          >
            <ChevronLeft />
          </button>
          <label
            className="date"
            aria-label={`Kiválasztott nap: ${selectedDateLabel}. Kattintson a naptár megnyitásához.`}
            onClick={() => {
              const input = dateInputRef.current;
              if (input && typeof (input as any).showPicker === 'function')
                (input as any).showPicker();
            }}
          >
            <CalendarDays />
            <span>
              <small>KIVÁLASZTOTT NAP</small>
              <strong>{selectedDateLabel}</strong>
            </span>
            <input
              ref={dateInputRef}
              type="date"
              className="date-input-native"
              value={selectedDate}
              onChange={(e) => e.target.value && setSelectedDate(e.target.value)}
            />
          </label>
          <button
            aria-label="Következő nap"
            onClick={() => setSelectedDate((date) => shiftDateKey(date, 1))}
          >
            <ChevronRight />
          </button>
          <button
            className="today"
            onClick={() => setSelectedDate(todayKey)}
            disabled={selectedDate === todayKey}
          >
            Ma
          </button>
          <span className="refresh">
            <RefreshCw className={loading ? 'spin' : ''} />
            {loading
              ? 'Betöltés…'
              : lastUpdated
                ? `Frissítve: ${lastUpdated.toLocaleTimeString('hu-HU')}`
                : 'Még nem frissült'}
          </span>
        </div>
        <div className="filters">
          <label className="search">
            <Search />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Keresés járat, sofőr vagy referencia alapján (minden napra)…"
            />
          </label>
          <select
            value={direction}
            onChange={(e) => setDirection(e.target.value)}
            aria-label="Irány"
          >
            <option value="ALL">Minden irány</option>
            <option value="AIRPORT_PICKUP">Reptéri felvétel</option>
            <option value="AIRPORT_DROPOFF">Reptérre szállítás</option>
          </select>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as TransferFlightStatus | 'ALL')}
            aria-label="Státusz"
          >
            <option value="ALL">Minden státusz</option>
            {(Object.keys(statusMap) as TransferFlightStatus[]).map((key) => (
              <option key={key} value={key}>
                {statusMap[key].label}
              </option>
            ))}
          </select>
        </div>
        {loading ? (
          <div className="empty">
            <RefreshCw className="spin" />
            <h3>Adatok betöltése…</h3>
            <p>A transzferek szinkronizálása folyamatban.</p>
          </div>
        ) : filtered.length ? (
          <>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>JÁRAT / ÚTVONAL</th>
                    {searching && <th>DÁTUM</th>}
                    <th>IRÁNY</th>
                    <th>JÁRAT IDŐPONTJA</th>
                    <th>VÁRHATÓ IDŐPONT</th>
                    <th>STÁTUSZ</th>
                    <th>SOFŐR / REFERENCIA</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((t) => (
                    <tr key={t.id}>
                      <td>
                        <strong className="flight">{t.flightNumber}</strong>
                        <small>
                          {t.route} · {t.passenger}
                        </small>
                      </td>
                      {searching && (
                        <td>
                          <strong>{shortDateLabel(t.serviceDate)}</strong>
                        </td>
                      )}
                      <td>
                        <span className="direction">
                          {t.direction === 'AIRPORT_PICKUP' ? (
                            <ArrowDownToLine />
                          ) : (
                            <ArrowUpFromLine />
                          )}
                          {t.direction === 'AIRPORT_PICKUP' ? 'Reptéri felvétel' : 'Reptérre'}
                        </span>
                      </td>
                      <td>
                        <strong>{t.scheduled}</strong>
                        <small>
                          {t.direction === 'AIRPORT_DROPOFF'
                            ? 'indulás · helyi idő'
                            : 'érkezés · helyi idő'}
                        </small>
                      </td>
                      <td>
                        <strong
                          className={t.estimatedDiffers ? 'time-diff' : undefined}
                          title={
                            t.estimatedDiffers
                              ? `Menetrend szerint ${t.providerScheduled}, eltér a rögzített időponttól (${t.scheduled})`
                              : undefined
                          }
                        >
                          {t.estimated}
                        </strong>
                      </td>
                      <td>
                        <FlightStatus
                          phase={t.phase}
                          delay={t.delay}
                          stale={t.stale}
                          delayUnknown={t.delayUnknown}
                          delayConflict={t.delayConflict}
                          probable={t.probable}
                          criticalThreshold={criticalThreshold}
                        />
                      </td>
                      <td>
                        <strong>{t.driver}</strong>
                        <small>{t.reference}</small>
                      </td>
                      <td>
                        <button
                          className="row-action"
                          aria-label="Megnyitás"
                          onClick={() => {
                            setEditing(t);
                            setModal(true);
                          }}
                        >
                          <ChevronRight />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="cards">
              {filtered.map((t) => (
                <article
                  className="transfer-card"
                  key={t.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => {
                    setEditing(t);
                    setModal(true);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setEditing(t);
                      setModal(true);
                    }
                  }}
                >
                  <div>
                    <strong className="flight">{t.flightNumber}</strong>
                    <FlightStatus
                      phase={t.phase}
                      delay={t.delay}
                      stale={t.stale}
                      delayUnknown={t.delayUnknown}
                      delayConflict={t.delayConflict}
                      probable={t.probable}
                      criticalThreshold={criticalThreshold}
                    />
                  </div>
                  <h3>{t.route}</h3>
                  <p>
                    {searching ? `${shortDateLabel(t.serviceDate)} · ` : ''}
                    {t.passenger} · {t.reference}
                  </p>
                  <div className="time-pair">
                    <span>
                      <small>TERVEZETT</small>
                      <strong>{t.scheduled}</strong>
                    </span>
                    <ArrowRight />
                    <span>
                      <small>VÁRHATÓ</small>
                      <strong
                        className={t.estimatedDiffers ? 'time-diff' : undefined}
                        title={
                          t.estimatedDiffers
                            ? `Menetrend szerint ${t.providerScheduled}, eltér a rögzített időponttól (${t.scheduled})`
                            : undefined
                        }
                      >
                        {t.estimated}
                      </strong>
                    </span>
                  </div>
                  <footer>
                    <span>
                      {t.direction === 'AIRPORT_PICKUP' ? <ArrowDownToLine /> : <ArrowUpFromLine />}
                      {t.driver}
                    </span>
                    <ChevronRight />
                  </footer>
                </article>
              ))}
            </div>
          </>
        ) : (
          <div className="empty">
            <Search />
            <h3>Nincs találat</h3>
            <p>Módosítsd a keresést vagy a szűrőket.</p>
          </div>
        )}
      </section>
      {modal && (
        <TransferModal
          initial={editing}
          defaultEmail={defaultEmail}
          defaultPickupBuffer={defaultPickupBuffer}
          onClose={() => {
            setModal(false);
            if (params.id) navigate('/daily', { replace: true });
          }}
          onRefresh={editing ? () => refreshTransferStatus(editing.id) : undefined}
          onDelete={
            editing
              ? async () => {
                  await removeTransfer(editing.id);
                  setModal(false);
                  if (params.id) navigate('/daily', { replace: true });
                }
              : undefined
          }
          onSave={async (d) => {
            const driver =
              d.driver || d.driverEmail
                ? { name: d.driver, email: d.driverEmail || undefined }
                : undefined;
            const flightTimeUtc = zonedTimeToUtcIso(d.date, d.flightTime, 'Europe/Budapest');
            await saveTransfer(
              {
                manual: {
                  flightNumber: d.flightNumber,
                  ...(d.origin && d.destination
                    ? {
                        originAirport: d.origin.toUpperCase(),
                        destinationAirport: d.destination.toUpperCase(),
                      }
                    : {}),
                  scheduledDepartureUtc: flightTimeUtc,
                  scheduledArrivalUtc: flightTimeUtc,
                },
                serviceDate: d.date,
                direction: d.direction,
                primaryEmail: d.email,
                recipients: d.driverEmail
                  ? [{ email: d.driverEmail, type: 'DRIVER', name: d.driver }]
                  : [],
                driver,
                reference: d.reference,
                passenger: d.passenger,
                pickupBuffer: d.pickupBuffer,
                notes: d.notes,
                active: true,
              },
              editing?.id,
            );
            setModal(false);
          }}
        />
      )}
    </main>
  );
}
function TransferModal({
  onClose,
  onSave,
  onDelete,
  onRefresh,
  initial,
  defaultEmail = '',
  defaultPickupBuffer = 35,
}: {
  onClose: () => void;
  onSave: (d: FormData) => Promise<void>;
  onDelete?: () => Promise<void>;
  onRefresh?: () => Promise<{
    computed: { scheduledEventUtc?: string | null; estimatedEventUtc?: string | null } | null;
    provider: unknown;
  }>;
  initial: Transfer | null;
  defaultEmail?: string;
  defaultPickupBuffer?: number;
}) {
  const [saveError, setSaveError] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState('');
  const [refreshedJustNow, setRefreshedJustNow] = useState(false);
  const [refreshedTimeLabel, setRefreshedTimeLabel] = useState('');
  const [lookingUp, setLookingUp] = useState(false);
  const [lookupError, setLookupError] = useState('');
  const [lookupNote, setLookupNote] = useState('');
  const [statusLog, setStatusLog] = useState<StatusLogEntry[]>([]);
  const [showStatusLog, setShowStatusLog] = useState(false);
  const transferId = initial?.id;
  useEffect(() => {
    if (!transferId) return;
    // A missing/unreadable log just keeps the history button hidden.
    return subscribeStatusLog(transferId, setStatusLog, () => setStatusLog([]));
  }, [transferId]);
  const {
    register,
    handleSubmit,
    watch,
    getValues,
    setValue,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      date: initial?.serviceDate || localDateKey(),
      flightNumber: initial?.flightNumber || '',
      origin: initial?.route.split(' → ')[0] || '',
      destination: initial?.route.split(' → ')[1] || '',
      flightTime: initial?.flightTime || '',
      direction: initial?.direction || 'AIRPORT_PICKUP',
      email: initial?.primaryEmail || defaultEmail,
      pickupBuffer: initial?.pickupBuffer ?? defaultPickupBuffer,
      driver: initial?.driver === 'Nincs kiosztva' ? '' : initial?.driver || '',
      driverEmail: initial?.driverEmail || '',
      reference: initial?.reference === '—' ? '' : initial?.reference || '',
      passenger: initial?.passenger === '—' ? '' : initial?.passenger || '',
      notes: initial?.notes || '',
    },
  });
  const selectedDirection = watch('direction');
  const submit = handleSubmit(async (data) => {
    try {
      setSaving(true);
      setSaveError('');
      await onSave(data);
    } catch {
      setSaveError('A transzfer mentése nem sikerült. Ellenőrizd az adatokat és próbáld újra.');
    } finally {
      setSaving(false);
    }
  });
  const handleDelete = async () => {
    if (!onDelete) return;
    setConfirmingDelete(false);
    try {
      setDeleting(true);
      setSaveError('');
      await onDelete();
    } catch {
      setSaveError('A transzfer törlése nem sikerült.');
    } finally {
      setDeleting(false);
    }
  };
  const handleRefresh = async () => {
    if (!onRefresh) return;
    try {
      setRefreshing(true);
      setRefreshError('');
      setRefreshedJustNow(false);
      setRefreshedTimeLabel('');
      const result = await onRefresh();
      const liveUtc = result.computed?.estimatedEventUtc || result.computed?.scheduledEventUtc;
      if (liveUtc) setRefreshedTimeLabel(timePart(liveUtc));
      setRefreshedJustNow(true);
    } catch {
      setRefreshError('Az aktuális adatok lekérdezése nem sikerült.');
    } finally {
      setRefreshing(false);
    }
  };
  const handleLookup = async () => {
    const { flightNumber, date } = getValues();
    if (!flightNumber.trim() || !date) {
      setLookupError('Adj meg járatszámot és dátumot a lekérdezéshez.');
      setLookupNote('');
      return;
    }
    try {
      setLookingUp(true);
      setLookupError('');
      setLookupNote('');
      const { matches } = await lookupFlight(flightNumber.trim(), date);
      const eventOf = (m: (typeof matches)[number]) =>
        selectedDirection === 'AIRPORT_DROPOFF' ? m.departure : m.arrival;
      // Several flights on the same number and day: take the one closest to an already entered
      // time, else the first.
      const { flightTime } = getValues();
      const enteredMs = flightTime
        ? Date.parse(zonedTimeToUtcIso(date, flightTime, 'Europe/Budapest'))
        : NaN;
      const distance = (m: (typeof matches)[number]) => {
        const ms = Date.parse(eventOf(m).scheduledUtc ?? '');
        return Number.isNaN(ms) || Number.isNaN(enteredMs) ? Infinity : Math.abs(ms - enteredMs);
      };
      const match = [...matches].sort((a, b) => distance(a) - distance(b))[0];
      if (!match) {
        setLookupError('Nem található járat a megadott számmal és dátummal.');
        return;
      }
      // The flight time field is the planned time, so it takes the schedule, never an estimate: a
      // delay would otherwise be saved as the entered time.
      const scheduledUtc = eventOf(match).scheduledUtc;
      if (scheduledUtc) setValue('flightTime', timePart(scheduledUtc), { shouldDirty: true });
      setValue('origin', match.departure.airport, { shouldDirty: true });
      setValue('destination', match.arrival.airport, { shouldDirty: true });
      setLookupNote(
        matches.length > 1
          ? `${matches.length} találat, ${Number.isNaN(enteredMs) ? 'az első' : 'a megadott időponthoz legközelebbi'} alapján töltöttük ki az adatokat. Ellenőrizd az időpontot.`
          : scheduledUtc
            ? 'A menetrend szerinti időpont és az útvonal kitöltve az API adatai alapján.'
            : 'A járatot megtaláltuk, de nincs időpont adat hozzá. Add meg kézzel.',
      );
    } catch {
      setLookupError('A járatadatok lekérdezése nem sikerült.');
    } finally {
      setLookingUp(false);
    }
  };
  const showProviderInfo =
    Boolean(initial) &&
    (Boolean(onRefresh) ||
      initial?.status === 'DATA_STALE' ||
      initial?.status === 'DELAYED' ||
      Boolean(initial?.providerLastError));
  return (
    <>
      <div className="modal-bg" role="dialog" aria-modal="true">
        <form className="modal" onSubmit={submit}>
          <header>
            <div>
              <span className="eyebrow dark">ÚJ MŰVELET</span>
              <h2>{initial ? 'Transzfer szerkesztése' : 'Transzfer rögzítése'}</h2>
            </div>
            <button type="button" onClick={onClose} aria-label="Bezárás">
              <X />
            </button>
          </header>
          <div className="form-body">
            <fieldset>
              <legend>Járat és időpont</legend>
              <div className="form-grid">
                <Field label="Dátum" error={errors.date?.message}>
                  <input type="date" {...register('date')} />
                </Field>
                <Field label="Járatszám" error={errors.flightNumber?.message}>
                  <input placeholder="pl. LH 1334" {...register('flightNumber')} />
                </Field>
                {initial && (
                  <Field label="Útvonal">
                    <input value={initial.route} disabled readOnly />
                  </Field>
                )}
                <Field label="Irány">
                  <select {...register('direction')}>
                    <option value="AIRPORT_PICKUP">Reptér → úti cél</option>
                    <option value="AIRPORT_DROPOFF">Felvételi cím → reptér</option>
                  </select>
                </Field>
                <Field
                  label={
                    selectedDirection === 'AIRPORT_DROPOFF'
                      ? 'Járat indulási időpontja'
                      : 'Járat érkezési időpontja'
                  }
                  error={errors.flightTime?.message}
                >
                  <input type="time" {...register('flightTime')} />
                </Field>
              </div>
              {!initial && (
                <div className="lookup-row">
                  <button
                    type="button"
                    className="secondary provider-refresh"
                    onClick={handleLookup}
                    disabled={lookingUp}
                  >
                    <RefreshCw size={14} className={lookingUp ? 'spin' : undefined} />
                    {lookingUp ? 'Lekérdezés…' : 'Időpont lekérdezése az API-ból'}
                  </button>
                  {lookupError && <small className="provider-info-error">{lookupError}</small>}
                  {lookupNote && !lookupError && (
                    <small className="provider-info-success">{lookupNote}</small>
                  )}
                </div>
              )}
            </fieldset>
            {showProviderInfo && (
              <fieldset className="provider-info">
                <legend>Szolgáltatói adat</legend>
                <div className="provider-info-top">
                  <div className="provider-info-body">
                    <Gauge />
                    <div>
                      {initial?.status === 'DATA_STALE' ? (
                        <strong>Nincs friss adat a szolgáltatótól erre a járatra.</strong>
                      ) : (
                        <strong>A járat késését a szolgáltató adata alapján számoljuk.</strong>
                      )}
                      <p>
                        Várható időpont: <strong>{initial?.estimated || '—'}</strong>
                      </p>
                      <p>
                        {typeof initial?.providerMatchCount === 'number'
                          ? `${initial.providerMatchCount} találat a szolgáltatónál`
                          : 'Nincs információ a találatok számáról'}
                        {initial?.providerLastCheckedAt
                          ? ` · Utolsó ellenőrzés: ${initial.providerLastCheckedAt.toLocaleTimeString('hu-HU', { hour: '2-digit', minute: '2-digit' })}`
                          : ' · Még nem történt ellenőrzés'}
                      </p>
                      {initial?.probable && (
                        <p className="provider-info-probable">
                          Beérkező gép
                          {initial.probable.reg ? ` (${initial.probable.reg})` : ''}:{' '}
                          {initial.probable.minutes > 0
                            ? `~${initial.probable.minutes} perc valószínű késés. `
                            : 'nem okoz késést. '}
                          {probableDelayDescription(initial.probable)}
                          {initial.probable.aircraftChanged && ' A járatot közben másik gép kapta.'}
                        </p>
                      )}
                      {initial?.providerLastError && (
                        <p className="provider-info-error">Hiba: {initial.providerLastError}</p>
                      )}
                      {refreshError && <p className="provider-info-error">{refreshError}</p>}
                      {refreshedJustNow && !refreshError && (
                        <p className="provider-info-success">
                          {refreshedTimeLabel
                            ? `Adatok frissítve, a várható időpont: ${refreshedTimeLabel}.`
                            : 'Adatok frissítve, de a szolgáltatónál nincs időpont adat ehhez a járathoz.'}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="provider-actions">
                    {onRefresh && (
                      <button
                        type="button"
                        className="secondary provider-refresh"
                        onClick={handleRefresh}
                        disabled={refreshing}
                      >
                        <RefreshCw size={14} className={refreshing ? 'spin' : undefined} />
                        {refreshing ? 'Lekérdezés…' : 'Adatok lekérdezése'}
                      </button>
                    )}
                    {statusLog.length > 0 && (
                      <button
                        type="button"
                        className="secondary provider-refresh"
                        onClick={() => setShowStatusLog(true)}
                      >
                        <FileClock size={14} />
                        Státusztörténet
                      </button>
                    )}
                  </div>
                </div>
              </fieldset>
            )}
            <fieldset>
              <legend>Transzfer részletei</legend>
              <div className="form-grid">
                <Field label="Utas / foglalás">
                  <input {...register('passenger')} />
                </Field>
                <Field label="Belső referencia">
                  <input {...register('reference')} />
                </Field>
                <Field label="Sofőr neve">
                  <input {...register('driver')} />
                </Field>
                <Field label="Sofőr e-mail (opcionális)" error={errors.driverEmail?.message}>
                  <input {...register('driverEmail')} />
                </Field>
              </div>
            </fieldset>
            <fieldset>
              <legend>Értesítések</legend>
              <Field label="Elsődleges értesítési e-mail" error={errors.email?.message}>
                <input {...register('email')} />
              </Field>
            </fieldset>
          </div>
          <footer>
            {saveError && (
              <div className="login-error" role="alert">
                {saveError}
              </div>
            )}
            {initial && (
              <button
                type="button"
                className="danger-button"
                onClick={() => setConfirmingDelete(true)}
                disabled={deleting || saving}
              >
                <Trash2 />
                {deleting ? 'Törlés…' : 'Transzfer törlése'}
              </button>
            )}
            <button type="button" className="secondary" onClick={onClose}>
              Mégse
            </button>
            <button className="primary" disabled={saving}>
              <Check />
              {saving ? 'Mentés…' : initial ? 'Változások mentése' : 'Mentés és járatkeresés'}
            </button>
          </footer>
        </form>
      </div>
      {showStatusLog && statusLog.length > 0 && (
        <StatusLogDialog
          flightNumber={initial?.flightNumber ?? ''}
          entries={statusLog}
          onClose={() => setShowStatusLog(false)}
        />
      )}
      {confirmingDelete && (
        <ConfirmDialog
          title="Transzfer törlése"
          message="Biztosan törlöd ezt a teljes transzferbejegyzést? Ez a művelet nem vonható vissza."
          confirmLabel={deleting ? 'Törlés…' : 'Végleges törlés'}
          onCancel={() => setConfirmingDelete(false)}
          onConfirm={handleDelete}
        />
      )}
    </>
  );
}
const statusLogTriggerLabel: Record<string, string> = {
  CREATE: 'Transzfer létrehozása',
  SCHEDULED_POLL: 'Ütemezett lekérdezés',
  WEBHOOK: 'Webhook értesítés',
  MANUAL_REFRESH: 'Kézi frissítés',
  USER_EDIT: 'Transzfer módosítása',
  INBOUND_CHECK: 'Beérkező gép ellenőrzése',
};
const statusLogSourceLabel: Record<string, string> = {
  AERODATABOX_API: 'AeroDataBox API',
  AERODATABOX_WEBHOOK: 'AeroDataBox webhook',
  INBOUND_AIRCRAFT: 'Beérkező gép · nem megerősített',
  USER: 'Felhasználó',
};
const statusLogFieldLabel: Record<string, string> = {
  status: 'Státusz',
  phase: 'Fázis',
  delayMinutes: 'Késés',
  delayUnknown: 'Késés ideje ismeretlen',
  stale: 'Elavult adat',
  estimatedEventUtc: 'Várható időpont',
  active: 'Figyelés',
  error: 'Hiba',
  flightNumber: 'Járatszám',
  serviceDate: 'Dátum',
  scheduledEventUtc: 'Menetrend szerinti időpont',
  probableDelayMinutes: 'Valószínű késés',
  inboundReg: 'Beérkező gép',
};
const statusLogDateTimeFormatter = new Intl.DateTimeFormat('hu-HU', {
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
  timeZone: 'Europe/Budapest',
});
function formatStatusLogValue(field: string, value: unknown) {
  if (value === null || value === undefined || value === '') return '—';
  if (field === 'status') return statusMap[value as TransferFlightStatus]?.label ?? String(value);
  if (field === 'phase') return phaseMap[value as TransferPhase]?.label ?? String(value);
  if (field === 'delayMinutes' || field === 'probableDelayMinutes') return `${value} perc`;
  if (field === 'active') return value ? 'aktív' : 'leállt';
  if (typeof value === 'boolean') return value ? 'igen' : 'nem';
  if (
    (field === 'estimatedEventUtc' ||
      field === 'scheduledEventUtc' ||
      field === 'revisedEventUtc') &&
    typeof value === 'string'
  )
    return Number.isNaN(Date.parse(value))
      ? value
      : statusLogDateTimeFormatter.format(new Date(value));
  return String(value);
}
const statusLogEvidenceLabel: Record<string, string> = {
  providerStatus: 'Szolgáltatói státusz',
  matchCount: 'Találatok száma',
  scheduledEventUtc: 'Menetrend szerinti időpont',
  revisedEventUtc: 'Megerősített időpont (ebből számolunk késést)',
  estimateDerived: 'Számolt (nem a szolgáltató által adott) időpont',
  rawDelayMinutes: 'Szolgáltató szerinti késés',
  delayHeld: 'Korábbi késés megtartva',
  delayConflict: 'Ellentmondó szolgáltatói adat',
  aircraftReg: 'Lajstromjel',
  confidence: 'Megbízhatóság',
  reason: 'Indok',
  inboundFlightNumber: 'Beérkező járat',
  inboundFrom: 'Honnan',
  inboundEstimatedArrivalUtc: 'Beérkező gép várható érkezése',
  upstreamFlightNumber: 'Késés forrása (korábbi út)',
  checkpointHours: 'Ellenőrzési pont (óra érkezés előtt)',
  error: 'Hibaüzenet',
};
function formatStatusLogEvidence(key: string, value: unknown) {
  if (key === 'confidence') return confidenceLabel[String(value)] ?? String(value);
  if (key === 'rawDelayMinutes') return `${value} perc`;
  return formatStatusLogValue(key, value);
}
function StatusLogDialog({
  flightNumber,
  entries,
  onClose,
}: {
  flightNumber: string;
  entries: StatusLogEntry[];
  onClose: () => void;
}) {
  return (
    <div className="modal-bg" role="dialog" aria-modal="true">
      <div className="modal status-log-dialog">
        <header>
          <div>
            <span className="eyebrow dark">{flightNumber}</span>
            <h2>Státusztörténet</h2>
          </div>
          <button type="button" onClick={onClose} aria-label="Bezárás">
            <X />
          </button>
        </header>
        <div className="form-body">
          <p className="status-log-note">
            Csak a változások szerepelnek, az utolsó 30 napból, a legfrissebb felül.
          </p>
          <ol className="status-log">
            {entries.map((entry) => {
              const evidence = Object.entries(entry.evidence).filter(
                ([, value]) => value !== null && value !== undefined && value !== false,
              );
              return (
                <li key={entry.id} className={`status-log-entry src-${entry.source.toLowerCase()}`}>
                  <div className="status-log-head">
                    <time>{entry.at ? statusLogDateTimeFormatter.format(entry.at) : '…'}</time>
                    <span className="status-log-source">
                      {statusLogSourceLabel[entry.source] ?? entry.source}
                    </span>
                    <span className="status-log-trigger">
                      {statusLogTriggerLabel[entry.trigger] ?? entry.trigger}
                      {entry.actor?.email ? ` · ${entry.actor.email}` : ''}
                    </span>
                  </div>
                  <ul className="status-log-changes">
                    {entry.changes.map((change) => (
                      <li key={change.field}>
                        <span>{statusLogFieldLabel[change.field] ?? change.field}:</span>{' '}
                        {entry.trigger === 'CREATE' ? (
                          <strong>{formatStatusLogValue(change.field, change.to)}</strong>
                        ) : (
                          <>
                            {formatStatusLogValue(change.field, change.from)} →{' '}
                            <strong>{formatStatusLogValue(change.field, change.to)}</strong>
                          </>
                        )}
                      </li>
                    ))}
                  </ul>
                  {evidence.length > 0 && (
                    <details>
                      <summary>Részletek</summary>
                      <dl>
                        {evidence.map(([key, value]) => (
                          <React.Fragment key={key}>
                            <dt>{statusLogEvidenceLabel[key] ?? key}</dt>
                            <dd>{formatStatusLogEvidence(key, value)}</dd>
                          </React.Fragment>
                        ))}
                      </dl>
                    </details>
                  )}
                </li>
              );
            })}
          </ol>
        </div>
      </div>
    </div>
  );
}
function ConfirmDialog({
  title,
  message,
  confirmLabel,
  onConfirm,
  onCancel,
}: {
  title: string;
  message: string;
  confirmLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <div className="modal-bg" role="alertdialog" aria-modal="true">
      <div className="modal confirm-dialog">
        <header>
          <div>
            <span className="eyebrow dark">MEGERŐSÍTÉS SZÜKSÉGES</span>
            <h2>{title}</h2>
          </div>
          <button type="button" onClick={onCancel} aria-label="Bezárás">
            <X />
          </button>
        </header>
        <div className="form-body">
          <p>{message}</p>
        </div>
        <footer>
          <button type="button" className="secondary" onClick={onCancel}>
            Mégse
          </button>
          <button type="button" className="danger-button" onClick={onConfirm}>
            <Trash2 />
            {confirmLabel}
          </button>
        </footer>
      </div>
    </div>
  );
}
function Field({
  label,
  error,
  badge,
  children,
}: {
  label: string;
  error?: string;
  badge?: string;
  children: React.ReactNode;
}) {
  return (
    <label className={error ? 'field invalid' : 'field'}>
      <span>
        {label}
        {badge && <b className="field-badge">{badge}</b>}
      </span>
      {children}
      {error && <small>{error}</small>}
    </label>
  );
}
function UserSettings({ user }: { user: User }) {
  return (
    <GenericPage eyebrow="SZEMÉLYES PROFIL" title="Beállításaim" text="Értesítések és megjelenítés">
      <SettingsForm user uid={user.uid} defaultEmail={user.email ?? ''} />
    </GenericPage>
  );
}
function AdminSettings() {
  return (
    <GenericPage
      eyebrow="RENDSZERKONFIGURÁCIÓ"
      title="Globális beállítások"
      text="Késési küszöbök és szolgáltatási alapértékek"
    >
      <SettingsForm />
    </GenericPage>
  );
}
function SettingsForm({
  user = false,
  uid,
  defaultEmail = '',
}: {
  user?: boolean;
  uid?: string;
  defaultEmail?: string;
}) {
  const [saved, setSaved] = useState(false);
  const [testSent, setTestSent] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);
  const defaults: Record<string, string | boolean> = user
    ? {
        primaryEmail: defaultEmail,
        secondaryEmail: '',
      }
    : {
        mildDelay: '15',
        operationalDelay: '30',
        criticalDelay: '60',
        severeDelay: '120',
        pollingEnabled: true,
        webhookEnabled: true,
        inboundTrackingEnabled: true,
      };
  const [values, setValues] = useState(defaults);
  useEffect(() => {
    loadSettings(user ? 'user' : 'global', uid)
      .then((stored) => stored && setValues((current) => ({ ...current, ...stored })))
      .catch(() => setError('A beállítások betöltése nem sikerült.'));
  }, [uid, user]);
  const fields: [string, string, string?][] = user
    ? [
        ['primaryEmail', 'Alapértelmezett e-mail'],
        ['secondaryEmail', 'Másodlagos e-mail', 'Még nincs élesben bekötve'],
      ]
    : [
        ['mildDelay', 'Enyhe késés (perc)'],
        ['operationalDelay', 'Közepes késés (perc)'],
        ['criticalDelay', 'Kritikus késés (perc)'],
        ['severeDelay', 'Súlyos késés (perc)'],
      ];
  const handleSettingsSave = async () => {
    try {
      setSaving(true);
      setSaved(false);
      setTestSent(false);
      setError('');
      await saveSettings(user ? 'user' : 'global', values, uid);
      setSaved(true);
    } catch {
      setError('A beállítások mentése nem sikerült.');
    } finally {
      setSaving(false);
    }
  };
  const handleTestEmail = async () => {
    try {
      setTestingEmail(true);
      setSaved(false);
      setTestSent(false);
      setError('');
      await sendTestFlightEmail(user ? String(values.primaryEmail || defaultEmail) : undefined);
      setTestSent(true);
    } catch (emailError) {
      const code =
        typeof emailError === 'object' && emailError && 'code' in emailError
          ? String(emailError.code)
          : '';
      const message = normalizeErrorText(emailError instanceof Error ? emailError.message : '');
      let detail = [code.replace('functions/', ''), message].filter(Boolean).join(' - ');
      if (code === 'functions/internal') {
        try {
          const diagnostic = await diagnoseEmailConfig();
          detail = `${detail} | diagnosztika: ${JSON.stringify(diagnostic.data)}`;
        } catch {
          detail = `${detail} | diagnosztika nem erheto el`;
        }
      }
      setError(detail ? `Teszt e-mail sikertelen: ${detail}` : 'Teszt e-mail sikertelen.');
    } finally {
      setTestingEmail(false);
    }
  };
  return (
    <section className="settings-card">
      <div className="settings-head">
        <div className="icon-box">
          <Settings />
        </div>
        <div>
          <h2>{user ? 'Értesítési preferenciák' : 'Operációs küszöbök'}</h2>
          <p>
            {user
              ? 'Add meg, mikor és hova érkezzenek a figyelmeztetések.'
              : 'A változások minden új járateseménynél érvényesülnek.'}
          </p>
        </div>
      </div>
      <div className="settings-grid">
        {fields.map(([key, label, badge]) => (
          <Field key={key} label={label} badge={badge}>
            <input
              value={String(values[key] ?? '')}
              onChange={(event) => setValues({ ...values, [key]: event.target.value })}
            />
          </Field>
        ))}
      </div>
      {!user && (
        <div className="settings-grid">
          <Field
            label="Intervallumos lekérdezés"
            badge="Nem érinti a létrehozáskori és manuális lekérdezést"
          >
            <label className="toggle">
              <input
                type="checkbox"
                checked={Boolean(values.pollingEnabled ?? true)}
                onChange={(event) => setValues({ ...values, pollingEnabled: event.target.checked })}
              />
              <span />
            </label>
          </Field>
          <Field label="Webhook feliratkozások" badge="Kikapcsolva minden feliratkozás törlődik">
            <label className="toggle">
              <input
                type="checkbox"
                checked={Boolean(values.webhookEnabled ?? true)}
                onChange={(event) => setValues({ ...values, webhookEnabled: event.target.checked })}
              />
              <span />
            </label>
          </Field>
          <Field
            label="Beérkező gép figyelése"
            badge="Valószínű késés 3, 2 és 1 órával indulás előtt"
          >
            <label className="toggle">
              <input
                type="checkbox"
                checked={Boolean(values.inboundTrackingEnabled ?? true)}
                onChange={(event) =>
                  setValues({ ...values, inboundTrackingEnabled: event.target.checked })
                }
              />
              <span />
            </label>
          </Field>
        </div>
      )}
      {saved && (
        <div className="success">
          <Check />
          Beállítások mentve.
        </div>
      )}
      {testSent && (
        <div className="success">
          <Mail />
          Teszt e-mail elkuldve.
        </div>
      )}
      {error && (
        <div className="login-error" role="alert">
          {error}
        </div>
      )}
      <div className="settings-actions">
        {user && (
          <button className="secondary" onClick={handleTestEmail} disabled={testingEmail || saving}>
            <Mail />
            {testingEmail ? 'Teszt kuldese...' : 'Teszt e-mail'}
          </button>
        )}
        <button className="primary" onClick={handleSettingsSave} disabled={saving || testingEmail}>
          <Check />
          {saving ? 'Mentés…' : 'Változások mentése'}
        </button>
      </div>
    </section>
  );
}
function GenericPage({
  eyebrow,
  title,
  text,
  children,
}: {
  eyebrow: string;
  title: string;
  text: string;
  children: React.ReactNode;
}) {
  return (
    <main className="page">
      <div className="page-title">
        <div>
          <span className="eyebrow dark">{eyebrow}</span>
          <h1>{title}</h1>
          <p>{text}</p>
        </div>
      </div>
      {children}
    </main>
  );
}
function UsersPage() {
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [dataError, setDataError] = useState('');
  const [busyUid, setBusyUid] = useState('');
  useEffect(
    () =>
      subscribeUsers(
        (rows) => {
          setUsers(rows);
          setDataError('');
        },
        () => setDataError('A felhasználók betöltése nem sikerült.'),
      ),
    [],
  );
  const pendingCount = users.filter((user) => user.status === 'PENDING').length;
  const changeAccess = async (user: UserProfile, status: 'ACTIVE' | 'REJECTED' | 'SUSPENDED') => {
    try {
      setBusyUid(user.uid);
      setDataError('');
      await changeUserAccess(user.uid, status, user.role || 'OPERATOR');
    } catch {
      setDataError('A jogosultság módosítása nem sikerült.');
    } finally {
      setBusyUid('');
    }
  };
  return (
    <GenericPage
      eyebrow="HOZZÁFÉRÉS-KEZELÉS"
      title="Felhasználók"
      text="Jóváhagyások, szerepkörök és hozzáférések"
    >
      <section className="settings-card">
        <div className="notice">
          <AlertTriangle />
          <div>
            <strong>{pendingCount} jóváhagyásra váró felhasználó</strong>
            <p>Ellenőrizd a hozzáférési kérelmeket.</p>
          </div>
        </div>
        {dataError && (
          <div className="login-error" role="alert">
            {dataError}
          </div>
        )}
        <div className="user-list">
          {users.map((u) => (
            <div className="user-row" key={u.uid}>
              <div className="avatar">
                {u.displayName
                  .split(' ')
                  .map((x) => x[0])
                  .join('')}
              </div>
              <div>
                <strong>{u.displayName}</strong>
                <small>{u.email}</small>
              </div>
              <Status
                status={
                  u.status === 'ACTIVE'
                    ? 'ON_TIME'
                    : u.status === 'PENDING'
                      ? 'SCHEDULED'
                      : 'COMPLETED'
                }
              />
              {u.status === 'PENDING' ? (
                <div className="user-actions">
                  <button
                    className="secondary"
                    disabled={busyUid === u.uid}
                    onClick={() => changeAccess(u, 'REJECTED')}
                  >
                    Elutasítás
                  </button>
                  <button
                    className="primary"
                    disabled={busyUid === u.uid}
                    onClick={() => changeAccess(u, 'ACTIVE')}
                  >
                    <Check />
                    Jóváhagyás
                  </button>
                </div>
              ) : (
                <button
                  className="secondary"
                  disabled={busyUid === u.uid}
                  onClick={() => changeAccess(u, u.status === 'ACTIVE' ? 'SUSPENDED' : 'ACTIVE')}
                >
                  {u.status === 'ACTIVE' ? 'Felfüggesztés' : 'Aktiválás'}
                </button>
              )}
            </div>
          ))}
          {!users.length && !dataError && (
            <div className="empty">
              <Users />
              <h3>Nincs felhasználó</h3>
            </div>
          )}
        </div>
      </section>
    </GenericPage>
  );
}
function AeroPage() {
  const [subscriptions, setSubscriptions] = useState<TransferDocument[]>([]);
  const [webhookEvents, setWebhookEvents] = useState<TransferDocument[]>([]);
  const [credits, setCredits] = useState<AeroDataBoxCredits | null>(null);
  const [dataError, setDataError] = useState('');
  const [refreshedAt, setRefreshedAt] = useState(new Date());
  useEffect(
    () =>
      subscribeAeroStatus(
        ({ subscriptions: nextSubscriptions, events, credits: nextCredits }) => {
          setSubscriptions(nextSubscriptions);
          setWebhookEvents(events);
          setCredits(nextCredits);
          setDataError('');
          setRefreshedAt(new Date());
        },
        () => setDataError('Az AeroDataBox állapot betöltése nem sikerült.'),
      ),
    [],
  );
  const activeSubscriptions = subscriptions.filter((item) => item.status === 'ACTIVE');
  const errorSubscriptions = subscriptions.filter((item) => item.status === 'ERROR');
  const latestWebhook = webhookEvents
    .filter((item) => !item.source)
    .sort(
      (a, b) =>
        (dateFromFirestore(b.createdAt)?.getTime() ?? 0) -
        (dateFromFirestore(a.createdAt)?.getTime() ?? 0),
    )[0];
  const recentEvents = [
    ...subscriptions.map((item) => ({
      key: `subscription-${item.id}`,
      time: dateFromFirestore(item.updatedAt),
      label:
        item.status === 'ACTIVE'
          ? 'Subscription aktív'
          : item.status === 'ERROR'
            ? 'Subscription hiba'
            : 'Subscription igényelve',
      detail: `${item.flightNumber ?? item.id}${item.lastError ? ` · ${item.lastError}` : ''}`,
    })),
    ...webhookEvents.map((item) => {
      const source = item.source;
      return {
        key: `webhook-${item.id}`,
        time: dateFromFirestore(item.createdAt),
        label:
          source === 'RECONCILIATION'
            ? 'Státuszlekérdezés'
            : source === 'RECONCILIATION_ERROR'
              ? 'Státuszlekérdezési hiba'
              : 'Webhook fogadva',
        detail:
          source === 'RECONCILIATION'
            ? `${item.flightNumber ?? 'Járat'} · ${item.normalized?.selectedStatus ?? 'nincs találat'} · ${item.normalized?.delayMinutes ?? 0} perc`
            : source === 'RECONCILIATION_ERROR'
              ? `${item.flightNumber ?? 'Járat'} · ${item.normalized?.error ?? 'ismeretlen hiba'}`
              : item.normalized?.flights?.length
                ? item.normalized.flights
                    .map(
                      (f: { number?: string; status?: string }) =>
                        `${f.number ?? 'Járat'} · ${f.status ?? 'ismeretlen'}`,
                    )
                    .join(', ')
                : `${item.normalized?.flightCount ?? 0} járat · 204 No Content`,
      };
    }),
  ]
    .sort((a, b) => (b.time?.getTime() ?? 0) - (a.time?.getTime() ?? 0))
    .slice(0, 10);
  return (
    <GenericPage
      eyebrow="INTEGRÁCIÓS KÖZPONT"
      title="AeroDataBox"
      text="Flight Alert állapot és szolgáltatásfelügyelet"
    >
      <section className="metrics aero">
        <div>
          <span>SZOLGÁLTATÁS</span>
          <strong className="healthy">
            <i />
            {dataError ? 'Hiba' : 'Elérhető'}
          </strong>
          <small>
            {errorSubscriptions.length
              ? `${errorSubscriptions.length} hibás subscription`
              : 'Firestore kapcsolat aktív'}
          </small>
        </div>
        <div>
          <span>API KVÓTA</span>
          <strong
            className={
              typeof credits?.apiUsage?.apiCallsLeft === 'number'
                ? credits.apiUsage.apiCallsLeft < 1000
                  ? 'quota-critical'
                  : credits.apiUsage.apiCallsLeft < 2000
                    ? 'quota-warning'
                    : undefined
                : undefined
            }
          >
            {typeof credits?.apiUsage?.apiCallsLeft === 'number'
              ? credits.apiUsage.apiCallsLeft.toLocaleString('hu-HU')
              : '—'}
          </strong>
          <small>
            {credits?.apiUsageError
              ? `Lekérdezés sikertelen: ${credits.apiUsageError}`
              : credits?.apiUsage
                ? `${credits.apiUsage.apiCallsMade.toLocaleString('hu-HU')} felhasználva / ${credits.apiUsage.quota.toLocaleString('hu-HU')} keret`
                : 'Még nincs adat'}
          </small>
        </div>
        <div>
          <span>AKTÍV SUBSCRIPTION</span>
          <strong>{activeSubscriptions.length}</strong>
          <small>{subscriptions.length} járat regisztrálva</small>
          <small
            className={
              typeof credits?.alertCreditBalance?.creditsRemaining === 'number' &&
              credits.alertCreditBalance.creditsRemaining < 50
                ? 'quota-critical'
                : undefined
            }
          >
            {credits?.alertCreditError
              ? `Webhook kredit lekérdezése sikertelen: ${credits.alertCreditError}`
              : typeof credits?.alertCreditBalance?.creditsRemaining === 'number'
                ? `Webhook kredit: ${credits.alertCreditBalance.creditsRemaining.toLocaleString('hu-HU')}`
                : 'Webhook kredit: még nincs adat'}
          </small>
        </div>
        <div>
          <span>UTOLSÓ WEBHOOK</span>
          <strong>
            {dateFromFirestore(latestWebhook?.createdAt)?.toLocaleTimeString('hu-HU', {
              hour: '2-digit',
              minute: '2-digit',
            }) ?? '—'}
          </strong>
          <small>
            {latestWebhook
              ? `${latestWebhook.normalized?.flightCount ?? 0} járat · sikeres`
              : 'Még nem érkezett webhook'}
          </small>
        </div>
      </section>
      <section className="settings-card settings-card--wide">
        <div className="settings-head">
          <div className="icon-box">
            <Gauge />
          </div>
          <div>
            <h2>Szolgáltatási események</h2>
            <p>A legutóbbi provider-kommunikáció.</p>
          </div>
          <button className="primary" onClick={() => setRefreshedAt(new Date())}>
            <RefreshCw />
            Állapotfrissítés
          </button>
        </div>
        {dataError && (
          <div className="login-error" role="alert">
            {dataError}
          </div>
        )}
        {recentEvents.map((event) => (
          <div className="event" key={event.key}>
            <Clock3 />
            <strong>
              {event.time
                ? event.time.toLocaleString('hu-HU', {
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                  })
                : '—'}
            </strong>
            <span>{event.label}</span>
            <small>{event.detail}</small>
          </div>
        ))}
        {!recentEvents.length && !dataError && (
          <div className="empty">
            <CloudCog />
            <h3>Még nincs szolgáltatási esemény</h3>
            <p>Az ütemezett ellenőrzés 15 percenként fut.</p>
          </div>
        )}
        <small>Utolsó kliensfrissítés: {refreshedAt.toLocaleTimeString('hu-HU')}</small>
      </section>
    </GenericPage>
  );
}
function AuditPage() {
  const [logs, setLogs] = useState<AuditLogDocument[]>([]);
  const [search, setSearch] = useState('');
  const [action, setAction] = useState('ALL');
  const [dataError, setDataError] = useState('');
  useEffect(
    () =>
      subscribeAuditLogs(
        (rows) => {
          setLogs(rows);
          setDataError('');
        },
        () => setDataError('Az auditnapló betöltése nem sikerült.'),
      ),
    [],
  );
  const actions = useMemo(
    () => Array.from(new Set(logs.map((item) => String(item.action ?? '')).filter(Boolean))).sort(),
    [logs],
  );
  const visibleLogs = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return logs.filter((item) => {
      const matchesAction = action === 'ALL' || item.action === action;
      const text = [item.action, auditActor(item), auditTarget(item)].join(' ').toLowerCase();
      return matchesAction && (!needle || text.includes(needle));
    });
  }, [action, logs, search]);
  return (
    <GenericPage
      eyebrow="BIZTONSÁG ÉS ELLENŐRZÉS"
      title="Auditnapló"
      text="A kritikus rendszeresemények megváltoztathatatlan története"
    >
      <section className="settings-card settings-card--wide">
        <div className="filters">
          <label className="search">
            <Search />
            <input
              placeholder="Keresés felhasználó vagy objektum alapján…"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </label>
          <select value={action} onChange={(event) => setAction(event.target.value)}>
            <option value="ALL">Minden művelet</option>
            {actions.map((item) => (
              <option value={item} key={item}>
                {item}
              </option>
            ))}
          </select>
        </div>
        {dataError && (
          <div className="login-error" role="alert">
            {dataError}
          </div>
        )}
        {visibleLogs.map((item) => (
          <div className="event audit-event" key={item.id}>
            <span className="audit-time">{auditTime(item)}</span>
            <strong>{item.action ?? 'UNKNOWN_ACTION'}</strong>
            <span>{auditActor(item)}</span>
            <small>{auditTarget(item)}</small>
          </div>
        ))}
        {!visibleLogs.length && !dataError && (
          <div className="empty">
            <FileClock />
            <h3>Nincs auditbejegyzés</h3>
            <p>
              {logs.length ? 'A szűrésre nincs találat.' : 'Valódi auditnapló még nem érkezett.'}
            </p>
          </div>
        )}
      </section>
    </GenericPage>
  );
}
type DailyStat = {
  key: string;
  label: string;
  count: number;
  avgDelay: number | null;
  maxDelay: number | null;
};
type StatsSummary = {
  days: DailyStat[];
  totalCount: number;
  overallAvgDelay: number | null;
  busiestDay: DailyStat | null;
  mostDelayedDay: DailyStat | null;
};
function buildDailyStats(transfers: TransferDocument[], daysCount: number): StatsSummary {
  const now = new Date();
  const buckets = new Map<
    string,
    {
      label: string;
      count: number;
      delaySum: number;
      delaySamples: number;
      maxDelay: number | null;
    }
  >();
  const order: string[] = [];
  for (let i = daysCount - 1; i >= 0; i -= 1) {
    const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() - i);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    buckets.set(key, {
      label: d.toLocaleDateString('hu-HU', { month: 'short', day: 'numeric' }),
      count: 0,
      delaySum: 0,
      delaySamples: 0,
      maxDelay: null,
    });
    order.push(key);
  }
  let overallDelaySum = 0;
  let overallDelaySamples = 0;
  transfers.forEach((item) => {
    const serviceDate = typeof item.serviceDate === 'string' ? item.serviceDate : '';
    const bucket = buckets.get(serviceDate);
    if (!bucket) return;
    bucket.count += 1;
    const delayMinutes = item.computed?.delayMinutes;
    if (typeof delayMinutes === 'number' && Number.isFinite(delayMinutes)) {
      bucket.delaySum += delayMinutes;
      bucket.delaySamples += 1;
      bucket.maxDelay =
        bucket.maxDelay === null ? delayMinutes : Math.max(bucket.maxDelay, delayMinutes);
      overallDelaySum += delayMinutes;
      overallDelaySamples += 1;
    }
  });
  const days = order.map((key) => {
    const bucket = buckets.get(key)!;
    return {
      key,
      label: bucket.label,
      count: bucket.count,
      avgDelay: bucket.delaySamples ? bucket.delaySum / bucket.delaySamples : null,
      maxDelay: bucket.maxDelay,
    };
  });
  const busiestDay = days.reduce<DailyStat | null>(
    (best, day) => (day.count > 0 && (!best || day.count > best.count) ? day : best),
    null,
  );
  const mostDelayedDay = days
    .filter((day) => day.maxDelay !== null)
    .reduce<DailyStat | null>(
      (worst, day) => (!worst || (day.maxDelay ?? 0) > (worst.maxDelay ?? 0) ? day : worst),
      null,
    );
  return {
    days,
    totalCount: days.reduce((sum, day) => sum + day.count, 0),
    overallAvgDelay: overallDelaySamples ? overallDelaySum / overallDelaySamples : null,
    busiestDay,
    mostDelayedDay,
  };
}
function StatsPage() {
  const [transfers, setTransfers] = useState<TransferDocument[]>([]);
  const [dataError, setDataError] = useState('');
  const [rangeDays, setRangeDays] = useState(30);
  useEffect(
    () =>
      subscribeTransfers(
        (rows) => {
          setTransfers(rows);
          setDataError('');
        },
        () => setDataError('A statisztikai adatok betöltése nem sikerült.'),
      ),
    [],
  );
  const stats = useMemo(() => buildDailyStats(transfers, rangeDays), [transfers, rangeDays]);
  const tickInterval = rangeDays > 14 ? Math.ceil(rangeDays / 12) - 1 : 0;
  return (
    <GenericPage
      eyebrow="ÜZEMELTETÉSI RIPORTOK"
      title="Statisztikák"
      text="Napi bontásban a rögzített transzferek száma és a járatok átlagos késése"
    >
      {dataError && (
        <div className="login-error" role="alert">
          {dataError}
        </div>
      )}
      <section className="metrics">
        <div>
          <span>ÖSSZES TRANSZFER</span>
          <strong>{stats.totalCount}</strong>
          <small>{rangeDays} nap alatt rögzítve</small>
        </div>
        <div>
          <span>ÁTLAGOS KÉSÉS</span>
          <strong
            className={
              stats.overallAvgDelay !== null && stats.overallAvgDelay >= 30 ? 'orange' : undefined
            }
          >
            {stats.overallAvgDelay !== null ? `${Math.round(stats.overallAvgDelay)} perc` : '—'}
          </strong>
          <small>A követett járatok alapján</small>
        </div>
        <div>
          <span>LEGFORGALMASABB NAP</span>
          <strong>{stats.busiestDay ? stats.busiestDay.count : '—'}</strong>
          <small>{stats.busiestDay ? stats.busiestDay.label : 'Nincs adat'}</small>
        </div>
        <div>
          <span>LEGTÖBB KÉSÉS</span>
          <strong className="orange">
            {stats.mostDelayedDay ? `${Math.round(stats.mostDelayedDay.maxDelay ?? 0)} perc` : '—'}
          </strong>
          <small>{stats.mostDelayedDay ? stats.mostDelayedDay.label : 'Nincs adat'}</small>
        </div>
      </section>
      <section className="settings-card settings-card--wide chart-card">
        <div className="filters">
          <select value={rangeDays} onChange={(event) => setRangeDays(Number(event.target.value))}>
            <option value={7}>Utolsó 7 nap</option>
            <option value={30}>Utolsó 30 nap</option>
            <option value={60}>Utolsó 60 nap</option>
          </select>
        </div>
        {stats.totalCount ? (
          <ResponsiveContainer width="100%" height={360}>
            <ComposedChart data={stats.days} margin={{ top: 10, right: 24, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#dce3e8" />
              <XAxis dataKey="label" tick={{ fontSize: 12 }} interval={tickInterval} />
              <YAxis yAxisId="count" allowDecimals={false} tick={{ fontSize: 12 }} />
              <YAxis
                yAxisId="delay"
                orientation="right"
                tick={{ fontSize: 12 }}
                unit=" p"
                allowDecimals={false}
              />
              <Tooltip
                formatter={(value: number, name: string) =>
                  name === 'Átlagos késés' || name === 'Maximális késés'
                    ? [`${Math.round(value)} perc`, name]
                    : [value, name]
                }
              />
              <Legend />
              <Bar
                yAxisId="count"
                dataKey="count"
                name="Rögzített transzferek"
                fill="#0b3a66"
                radius={[4, 4, 0, 0]}
              />
              <Line
                yAxisId="delay"
                type="monotone"
                dataKey="avgDelay"
                name="Átlagos késés"
                stroke="#e8ad46"
                strokeWidth={2.5}
                dot={{ r: 3 }}
                connectNulls
              />
              <Line
                yAxisId="delay"
                type="monotone"
                dataKey="maxDelay"
                name="Maximális késés"
                stroke="#c84646"
                strokeWidth={2}
                strokeDasharray="5 4"
                dot={{ r: 2.5 }}
                connectNulls
              />
            </ComposedChart>
          </ResponsiveContainer>
        ) : (
          !dataError && (
            <div className="empty">
              <Gauge />
              <h3>Nincs elegendő adat</h3>
              <p>Ehhez az időszakhoz még nincs rögzített transzfer.</p>
            </div>
          )
        )}
      </section>
    </GenericPage>
  );
}
function App() {
  const [user, setUser] = useState<User | null>(null);
  const [hasAccess, setHasAccess] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [authReady, setAuthReady] = useState(!firebaseConfigured);

  useEffect(
    () =>
      observeAuth((nextUser, approved, role) => {
        setUser(nextUser);
        setHasAccess(approved);
        setIsAdmin(role === 'admin');
        setAuthReady(true);
      }),
    [],
  );

  if (!authReady) {
    return <main className="auth-loading" aria-label="Bejelentkezés ellenőrzése" />;
  }

  return (
    <BrowserRouter>
      <Routes>
        <Route
          path="/login"
          element={user ? <Navigate to={hasAccess ? '/daily' : '/pending'} replace /> : <Login />}
        />
        <Route
          path="/pending"
          element={
            user ? (
              hasAccess ? (
                <Navigate to="/daily" replace />
              ) : (
                <Pending user={user} />
              )
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/*"
          element={
            user && hasAccess ? (
              <Shell user={user} isAdmin={isAdmin} />
            ) : (
              <Navigate to={user ? '/pending' : '/login'} replace />
            )
          }
        />
        <Route path="/" element={<Navigate to="/daily" />} />
      </Routes>
    </BrowserRouter>
  );
}
createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);
